import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, Zap, ShoppingCart, FileText, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { buyPackage } from '../lib/payments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_LABEL = {
  free: { name: 'Δωρεάν', price: '', vat: '' },
  limited: { name: 'Limited', price: '10 €/μήνα', vat: '(χωρίς ΦΠΑ)' },
  unlimited: { name: 'Πλήρης έκδοση', price: '240 €/έτος', vat: '(χωρίς ΦΠΑ)' },
  beta: { name: 'Beta Tester', price: 'Δωρεάν', vat: '(πρόσκληση)' },
};

const REASON_LABEL = {
  signup_bonus: '🎁 Δωρεάν μονάδες εγγραφής',
  entry_create: 'Νέα εγγραφή',
  financial_entry_create: 'Οικονομική καταχώρηση',
  postponement: 'Αναβολή',
  'status:ΜΑΤΑΙΩΣΗ': 'Ματαίωση',
  'status:ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ': 'Παραίτηση',
  admin_adjust: 'Προσθήκη από διαχειριστή',
  limited_plan_monthly_credits: 'Μηνιαίες μονάδες (Limited)',
  manual_topup: 'Αγορά πακέτου',
  stripe_purchase: 'Αγορά μέσω Stripe',
};

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return String(v); }
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('el-GR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return String(v); }
};

const SubscriptionTab = () => {
  const [info, setInfo] = useState(null);
  const [txs, setTxs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [b, t, inv] = await Promise.all([
        axios.get(`${API}/billing/me`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/billing/transactions?limit=20`, { withCredentials: true }).catch(() => ({ data: { transactions: [] } })),
        axios.get(`${API}/invoices/me`, { withCredentials: true }).catch(() => ({ data: { invoices: [] } })),
      ]);
      if (b) {
        setInfo(b.data);
      } else if (!info) {
        // Only show error if we don't already have data loaded
        toast.error('Αποτυχία φόρτωσης στοιχείων συνδρομής');
      }
      setTxs(t.data?.transactions || []);
      setInvoices(inv.data?.invoices || []);
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = async (packageId) => {
    setBuying(packageId);
    try {
      await buyPackage(packageId);
      // redirect happens inside buyPackage
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Αποτυχία έναρξης πληρωμής');
      setBuying(null);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !info) {
    return <div className="bg-white rounded-lg border border-slate-200 p-6 text-center text-slate-500">Φόρτωση...</div>;
  }

  if (!info) return null;

  const plan = info.subscription_plan || 'free';
  const unlimited = info.unlimited_active;
  const credits = info.credits ?? 0;

  return (
    <div className="space-y-5" data-testid="subscription-tab">
      {/* Header: current plan */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Η συνδρομή σου</h3>
          <p className="text-sm text-slate-500">Κατάσταση λογαριασμού και μονάδες χρήσης</p>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Credits */}
          <div className="rounded-lg border border-slate-200 p-4 bg-gradient-to-br from-amber-50 to-orange-50 text-center">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">
              Διαθεσιμες μοναδες
            </div>
            <div className="text-3xl font-bold text-slate-900" data-testid="credits-display">
              {info.is_beta_tester ? 'Beta' : (unlimited ? '∞' : credits)}
            </div>
            <div className="text-xs text-slate-600 mt-1">
              {info.is_beta_tester
                ? 'Απεριόριστη χρήση κατά το beta'
                : (unlimited ? 'Απεριόριστη χρήση ενεργή' : '5 μονάδες ανά νέα καταχώρηση ή AI οργάνωση — τα υπόλοιπα δωρεάν')}
            </div>
          </div>

          {/* Plan */}
          <div className="rounded-lg border border-slate-200 p-4 bg-gradient-to-br from-slate-50 to-slate-100 text-center">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">
              Πλανο
            </div>
            <div className="text-base sm:text-lg font-semibold text-slate-900 leading-tight">
              {PLAN_LABEL[plan].name}
            </div>
            {PLAN_LABEL[plan].price && (
              <div className="text-sm font-medium text-slate-700 mt-0.5">
                {PLAN_LABEL[plan].price}
              </div>
            )}
            {PLAN_LABEL[plan].vat && (
              <div className="text-xs text-slate-500 mt-0.5">{PLAN_LABEL[plan].vat}</div>
            )}
            <div className="text-xs text-slate-600 mt-1">
              {plan === 'free' && 'Δωρεάν — πληρώνεις όσο χρησιμοποιείς'}
              {plan === 'unlimited' && 'Απεριόριστες καταχωρήσεις'}
              {plan === 'beta' && 'Ευχαριστούμε που δοκιμάζεις το COURTPLAN!'}
            </div>
          </div>

          {/* Renewal */}
          <div className="rounded-lg border border-slate-200 p-4 text-center">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">
              Επομενη ανανεωση
            </div>
            <div className="text-xl font-semibold text-slate-900">
              {plan === 'free' ? '—' : fmtDate(info.subscription_end)}
            </div>
            <div className="text-xs text-slate-600 mt-1">
              {info.subscription_start ? `Έναρξη: ${fmtDate(info.subscription_start)}` : 'Χωρίς ενεργή συνδρομή'}
            </div>
          </div>
        </div>
      </div>

      {/* Available plans */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Πλάνα & Πακέτα</h3>
          <p className="text-sm text-slate-500">Επέλεξε το κατάλληλο για σένα</p>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <PlanCard
            title="Pay-per-use"
            price="20 €/100 μονάδες"
            vat="(χωρίς ΦΠΑ)"
            subtitle={`${info.pack_size || 100} μονάδες = 20 καταχωρήσεις`}
            features={[
              'Καμία μηνιαία δέσμευση',
              '5 μονάδες ανά νέα καταχώρηση ή χρήση AI Organiser',
              'Η αναβολή δημιουργεί νέα καταχώρηση (5 μονάδες)',
              'Αλλαγές κατάστασης και οικονομικά: δωρεάν',
              'Οι μονάδες δεν λήγουν',
            ]}
            cta="Αγορά πακέτου"
            onClick={() => handleBuy('credit_pack_100')}
            loading={buying === 'credit_pack_100'}
            testId="plan-card-payperuse"
          />
          <PlanCard
            title="Πλήρης έκδοση"
            price="240 €/έτος"
            vat="(χωρίς ΦΠΑ)"
            subtitle="Απεριόριστη χρήση"
            features={[
              'Απεριόριστες καταχωρήσεις & AI Organiser',
              'Όλα τα features',
              'Ιδανικό για σοβαρή χρήση',
            ]}
            cta="Ενεργοποίηση"
            onClick={() => handleBuy('unlimited_yearly')}
            loading={buying === 'unlimited_yearly'}
            highlight={unlimited}
            popular
            testId="plan-card-unlimited"
          />
        </div>
        <div className="px-5 pb-5 text-xs text-slate-500">
          💳 Ασφαλής πληρωμή μέσω Stripe. Τα τιμολόγια εκδίδονται αυτόματα.
        </div>
      </div>

      {/* Invoices */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-600" />
          <div>
            <h3 className="font-semibold text-slate-900">Τιμολόγια</h3>
            <p className="text-sm text-slate-500">Αυτόματη έκδοση μετά από κάθε πληρωμή</p>
          </div>
        </div>
        {invoices.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">Κανένα τιμολόγιο ακόμη.</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="invoice-list">
            {invoices.map((iv) => (
              <div key={iv.invoice_id} className="px-4 py-3 flex items-center justify-between text-sm gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">
                    {iv.full_number} · {iv.package_label}
                  </div>
                  <div className="text-xs text-slate-500">
                    {fmtDate(iv.issued_at)} · Καθαρό {Math.round(iv.amount_net || 0)} € + ΦΠΑ {Math.round(iv.amount_vat || 0)} €
                  </div>
                </div>
                <MyDataBadge status={iv.mydata_status} mark={iv.mydata_mark} />
                <div className="font-mono font-semibold text-slate-900 w-20 text-right">
                  {Math.round(iv.amount_gross || 0)} €
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MyDataBadge = ({ status, mark }) => {
  if (status === 'submitted' && mark) {
    return (
      <span title={`ΜΑΡΚ: ${mark}`} className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
        <CheckCircle2 className="w-3 h-3" /> myDATA
      </span>
    );
  }
  if (status === 'skipped') {
    return (
      <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">
        <AlertTriangle className="w-3 h-3" /> εκκρεμεί
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded">
        <XCircle className="w-3 h-3" /> αποτυχία
      </span>
    );
  }
  return null;
};

const PlanCard = ({ title, price, vat, subtitle, features, cta, highlight, popular, onClick, loading, testId }) => (
  <div
    className={`relative rounded-lg border p-4 text-center ${highlight ? 'border-red-700 bg-red-50' : 'border-slate-200 bg-white'}`}
    data-testid={testId}
  >
    {popular && !highlight && (
      <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-red-700 text-white text-xs px-2 py-0.5 rounded-full">Δημοφιλές</span>
    )}
    {highlight && (
      <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-700 text-white text-xs px-2 py-0.5 rounded-full">Ενεργό</span>
    )}
    <div className="text-sm font-medium text-slate-500">{title}</div>
    <div className="text-base sm:text-lg md:text-xl font-bold text-slate-900 mt-1 leading-tight whitespace-nowrap">{price}</div>
    {vat && <div className="text-xs font-normal text-slate-500 mt-0.5">{vat}</div>}
    <div className="text-xs text-slate-500 mb-3 mt-1">{subtitle}</div>
    <ul className="space-y-1.5 text-xs text-slate-700 mb-4 inline-block text-left">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-1.5">
          <span className="text-green-700 mt-0.5">✓</span>
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <Button
      type="button"
      size="sm"
      onClick={onClick}
      disabled={loading}
      className={`w-full h-9 ${highlight ? 'bg-red-900 hover:bg-red-800' : 'bg-red-700 hover:bg-red-800'} text-white disabled:opacity-50`}
      data-testid={`${testId}-buy-btn`}
    >
      <ShoppingCart className="w-3.5 h-3.5 mr-1" />
      {loading ? 'Παραπομπή...' : highlight ? 'Παράταση' : cta}
    </Button>
  </div>
);

export default SubscriptionTab;
